import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Chip,
  CircularProgress,
  alpha,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import proxy from '../api/axios';
import { resolveProfileImage } from '../common/utils';

const ACCENT = '#137274';

const PODIUM = [
  { rank: 1, color: '#EAB308', label: '1st', height: 88 },
  { rank: 2, color: '#94A3B8', label: '2nd', height: 68 },
  { rank: 3, color: '#D97706', label: '3rd', height: 56 },
];

const ContestResult = ({ contestId: contestIdProp, embedded = false }) => {
  const { contestId: contestIdParam } = useParams();
  const contestId = contestIdProp || contestIdParam;
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.12)';
  const surface = isDark ? '#161b22' : '#ffffff';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const pageBg = isDark
    ? '#0f1419'
    : 'linear-gradient(180deg, #eef6f8 0%, #f7f9fb 45%, #ffffff 100%)';

  const [contest, setContest] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!contestId) {
      setLoading(false);
      setError('Contest not found.');
      return;
    }

    let cancelled = false;
    const fetchResults = async () => {
      try {
        setLoading(true);
        setError('');
        const { data } = await proxy.get(`/contests/${contestId}/result`);
        if (cancelled) return;
        setContest(data.contest || null);
        setParticipants(Array.isArray(data.participants) ? data.participants : []);
      } catch (err) {
        console.error('Error fetching contest results:', err);
        if (!cancelled) setError('Could not load contest results.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchResults();
    return () => {
      cancelled = true;
    };
  }, [contestId]);

  const ranked = useMemo(
    () =>
      participants.map((p, index) => ({
        ...p,
        rank: index + 1,
        key: p.user?.id || index,
      })),
    [participants],
  );

  const top3 = ranked.slice(0, 3);
  // Podium visual order: 2nd, 1st, 3rd
  const podiumOrder = [top3[1], top3[0], top3[2]].filter(Boolean);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: embedded ? 240 : '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          bgcolor: embedded ? 'transparent' : pageBg,
        }}
      >
        <CircularProgress size={28} sx={{ color: ACCENT }} />
        <Typography sx={{ color: muted, fontWeight: 600 }}>Loading results…</Typography>
      </Box>
    );
  }

  const content = (
    <Box sx={{ maxWidth: embedded ? '100%' : 960, mx: 'auto', width: '100%' }}>
      {!embedded && (
        <Box sx={{ mb: 3 }}>
          <Button
            component={Link}
            to={`/contests/details/${contestId}`}
            startIcon={<ArrowBackIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: muted,
              mb: 1.5,
              px: 0,
              '&:hover': { bgcolor: 'transparent', color: ACCENT },
            }}
          >
            Contest details
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
            <Chip
              icon={<LeaderboardOutlinedIcon sx={{ fontSize: '16px !important' }} />}
              label="Results"
              size="small"
              sx={{
                fontWeight: 800,
                bgcolor: alpha(ACCENT, isDark ? 0.22 : 0.12),
                color: ACCENT,
              }}
            />
          </Box>
          <Typography
            component="h1"
            sx={{
              fontWeight: 900,
              letterSpacing: '-0.03em',
              fontSize: { xs: '1.5rem', sm: '1.85rem' },
              mb: 0.5,
            }}
          >
            {contest?.title || 'Contest results'}
          </Typography>
          <Typography sx={{ color: muted, fontSize: '0.92rem' }}>
            {ranked.length
              ? `${ranked.length} participant${ranked.length === 1 ? '' : 's'} · ranked by score`
              : 'Standings will appear here after submissions.'}
          </Typography>
        </Box>
      )}

      {error ? (
        <Box
          sx={{
            p: 3,
            borderRadius: 2.5,
            border: `1px solid ${border}`,
            bgcolor: surface,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Couldn’t load results</Typography>
          <Typography sx={{ color: muted }}>{error}</Typography>
        </Box>
      ) : ranked.length === 0 ? (
        <Box
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: 2.5,
            border: `1px solid ${border}`,
            bgcolor: surface,
            textAlign: 'center',
          }}
        >
          <EmojiEventsOutlinedIcon sx={{ fontSize: 40, color: muted, mb: 1 }} />
          <Typography sx={{ fontWeight: 800, mb: 0.5 }}>No standings yet</Typography>
          <Typography sx={{ color: muted, fontSize: '0.9rem' }}>
            Results show up once participants submit.
          </Typography>
        </Box>
      ) : (
        <>
          {/* Podium */}
          {top3.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                gap: { xs: 1, sm: 2 },
                mb: 3,
                px: { xs: 0.5, sm: 2 },
                pt: 1,
              }}
            >
              {podiumOrder.map((p) => {
                const meta = PODIUM.find((x) => x.rank === p.rank) || PODIUM[2];
                const isFirst = p.rank === 1;
                return (
                  <Box
                    key={p.key}
                    sx={{
                      flex: isFirst ? '1.15 1 0' : '1 1 0',
                      maxWidth: isFirst ? 220 : 180,
                      textAlign: 'center',
                    }}
                  >
                    <Avatar
                      src={resolveProfileImage(p.user?.profilePicture || p.user?.profileImage)}
                      alt={p.user?.name}
                      sx={{
                        width: isFirst ? 72 : 56,
                        height: isFirst ? 72 : 56,
                        mx: 'auto',
                        mb: 1,
                        border: `3px solid ${meta.color}`,
                        fontWeight: 800,
                        bgcolor: alpha(meta.color, 0.2),
                        color: isDark ? '#fff' : '#0f2a2b',
                      }}
                    >
                      {(p.user?.name || '?').charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: isFirst ? '0.95rem' : '0.85rem',
                        mb: 0.25,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        px: 0.5,
                      }}
                    >
                      {p.user?.name || 'Participant'}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.78rem',
                        color: muted,
                        mb: 1,
                      }}
                    >
                      {p.score} pts
                    </Typography>
                    <Box
                      sx={{
                        height: meta.height,
                        borderRadius: '12px 12px 4px 4px',
                        background: `linear-gradient(180deg, ${alpha(meta.color, isDark ? 0.45 : 0.55)} 0%, ${alpha(meta.color, isDark ? 0.18 : 0.22)} 100%)`,
                        border: `1px solid ${alpha(meta.color, 0.5)}`,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.25,
                      }}
                    >
                      <EmojiEventsOutlinedIcon sx={{ color: meta.color, fontSize: isFirst ? 28 : 22 }} />
                      <Typography sx={{ fontWeight: 900, fontSize: '0.85rem', color: meta.color }}>
                        {meta.label}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Full standings */}
          <Box
            sx={{
              borderRadius: 2.5,
              border: `1px solid ${border}`,
              bgcolor: surface,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: `1px solid ${border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Typography sx={{ fontWeight: 800 }}>Full standings</Typography>
              <Typography sx={{ fontSize: '0.78rem', color: muted, fontWeight: 600 }}>
                {ranked.length} ranked
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      '& th': {
                        fontWeight: 800,
                        fontSize: '0.72rem',
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        color: muted,
                        borderBottom: `1px solid ${border}`,
                        bgcolor: isDark ? alpha('#fff', 0.03) : alpha(ACCENT, 0.04),
                      },
                    }}
                  >
                    <TableCell width={72}>Rank</TableCell>
                    <TableCell>Participant</TableCell>
                    <TableCell align="right">Score</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ranked.map((p) => {
                    const podium = PODIUM.find((x) => x.rank === p.rank);
                    return (
                      <TableRow
                        key={p.key}
                        sx={{
                          '&:hover': {
                            bgcolor: isDark ? alpha('#fff', 0.03) : alpha(ACCENT, 0.04),
                          },
                          '& td': { borderBottom: `1px solid ${border}`, py: 1.25 },
                        }}
                      >
                        <TableCell>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 1.25,
                              display: 'grid',
                              placeItems: 'center',
                              fontWeight: 800,
                              fontSize: '0.85rem',
                              bgcolor: podium
                                ? alpha(podium.color, isDark ? 0.25 : 0.18)
                                : isDark
                                  ? alpha('#fff', 0.06)
                                  : alpha(ACCENT, 0.08),
                              color: podium ? podium.color : muted,
                            }}
                          >
                            {p.rank}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            <Avatar
                              src={resolveProfileImage(p.user?.profilePicture || p.user?.profileImage)}
                              alt={p.user?.name}
                              sx={{
                                width: 36,
                                height: 36,
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                bgcolor: alpha(ACCENT, 0.15),
                                color: ACCENT,
                              }}
                            >
                              {(p.user?.name || '?').charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.92rem' }}>
                              {p.user?.name || 'Participant'}
                            </Typography>
                            {p.rank <= 3 && (
                              <Chip
                                size="small"
                                label={PODIUM[p.rank - 1].label}
                                sx={{
                                  height: 22,
                                  fontWeight: 800,
                                  fontSize: '0.65rem',
                                  bgcolor: alpha(PODIUM[p.rank - 1].color, 0.15),
                                  color: PODIUM[p.rank - 1].color,
                                  display: { xs: 'none', sm: 'flex' },
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                            {p.score}
                            <Typography
                              component="span"
                              sx={{ ml: 0.5, fontWeight: 600, fontSize: '0.75rem', color: muted }}
                            >
                              pts
                            </Typography>
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {!embedded && (
            <Box sx={{ display: 'flex', gap: 1.5, mt: 3, flexWrap: 'wrap' }}>
              <Button
                component={Link}
                to="/contests"
                variant="outlined"
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  borderRadius: 2,
                  borderColor: border,
                  color: muted,
                }}
              >
                All contests
              </Button>
              <Button
                component={Link}
                to={`/contests/details/${contestId}`}
                variant="contained"
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  borderRadius: 2,
                  bgcolor: ACCENT,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#0f5c5e', boxShadow: 'none' },
                }}
              >
                Contest page
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );

  if (embedded) return content;

  return (
    <Box sx={{ minHeight: '100%', bgcolor: pageBg, py: { xs: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      <Helmet>
        <title>
          {contest?.title ? `${contest.title} · Results` : 'Contest Results'} | CrafterHack
        </title>
      </Helmet>
      {content}
    </Box>
  );
};

export default ContestResult;
